import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ArrowLeft, Pencil, Buildings, Bank, IdentificationCard, Trash, CheckCircle, ArrowCounterClockwise, Clock } from '@phosphor-icons/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTenantPath } from '../lib/tenantPath';
import useAuthStore from '../store/authStore';
import PrevNextNav from '../components/PrevNextNav';
import SCViewOnlyBanner from '../components/SCViewOnlyBanner';
import { ScGatedButton } from '../components/ScGatedButton';

import { confirm } from '../components/ConfirmDialog';
import { notify } from '../lib/notify';
import { PageSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const tp = useTenantPath();
  const { user } = useAuthStore();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [gstStatus, setGstStatus] = useState(null);
  const [gstChecking, setGstChecking] = useState(false);

  const canApproveBankAccounts = ['Vice President', 'President'].includes(user?.role);
  // iter132.3 — Only VP/President can revoke a customer-scoped terms
  // template; other roles see the section read-only for context.
  const canRevokeTermsTemplate = ['Vice President', 'President'].includes(user?.role);

  // iter132.3 — Saved payment-terms templates for THIS customer.
  // iter132.4 — Two lists: active + recently-revoked (28-day undo window).
  const [termsTemplates, setTermsTemplates] = useState([]);
  const [termsRevoked, setTermsRevoked] = useState([]);
  const [termsBusyId, setTermsBusyId] = useState(null);
  const fetchTermsTemplates = useCallback(async () => {
    if (!id) return;
    try {
      const [active, all] = await Promise.all([
        axios.get(`${API}/customer-terms-templates`, { params: { customer_id: id } }),
        axios.get(`${API}/customer-terms-templates`, { params: { customer_id: id, include_revoked: true } }),
      ]);
      const activeItems = active.data?.items || [];
      setTermsTemplates(activeItems);
      const activeIds = new Set(activeItems.map((r) => r.id));
      setTermsRevoked((all.data?.items || []).filter((r) => r.revoked_at && !activeIds.has(r.id)));
    } catch {
      setTermsTemplates([]);
      setTermsRevoked([]);
    }
  }, [id]);
  useEffect(() => { fetchTermsTemplates(); }, [fetchTermsTemplates]);

  const handleRevokeTemplate = async (tpl) => {
    if (!canRevokeTermsTemplate) return;
    const ok = await confirm({ message: 
      `Revoke saved payment terms "${tpl.payment_terms_summary}" for this customer?\n\n` +
      `You'll have 28 days to restore before it's permanently deleted.`,
     });
    if (!ok) return;
    setTermsBusyId(tpl.id);
    try {
      await axios.delete(`${API}/customer-terms-templates/${tpl.id}`);
      await fetchTermsTemplates();
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to revoke template');
    } finally {
      setTermsBusyId(null);
    }
  };

  const handleRestoreTemplate = async (tpl) => {
    if (!canRevokeTermsTemplate) return;
    setTermsBusyId(tpl.id);
    try {
      await axios.post(`${API}/customer-terms-templates/${tpl.id}/restore`);
      await fetchTermsTemplates();
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to restore template');
    } finally {
      setTermsBusyId(null);
    }
  };

  useEffect(() => {
    fetchCustomer();
  }, [id]);

  // Feb 2026 (iter85) — book-style navigation between customers.
  const [customerList, setCustomerList] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/customers`).then((r) => {
      if (!cancelled) setCustomerList(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const navItems = customerList.map((c) => ({
    id: c.id,
    label: c.organisation_name || c.customer_code || '',
  }));

  useEffect(() => {
    if (customer?.gst_number && customer.gst_number.length === 15) {
      checkGstFiling(customer.gst_number);
    }
  }, [customer?.gst_number]);

  const checkGstFiling = async (gstin) => {
    setGstChecking(true);
    try {
      const resp = await axios.get(`${API}/gst/verify/${gstin}`);
      setGstStatus(resp.data);
    } catch { setGstStatus(null); }
    finally { setGstChecking(false); }
  };

  const fetchCustomer = async () => {
    try {
      const response = await axios.get(`${API}/customers/${id}`);
      setCustomer(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching customer:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/customers/${id}`, {
        organisation_name: formData.organisation_name,
        md_ceo_name: formData.md_ceo_name,
        poc_name: formData.poc_name,
        poc_designation: formData.poc_designation,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        gst_number: formData.gst_number,
        pan_number: formData.pan_number,
        bank_accounts: formData.bank_accounts,
        industry_type: formData.industry_type,
        website: formData.website,
        notes: formData.notes
      });
      setEditing(false);
      fetchCustomer();
    } catch (error) {
      console.error('Error updating customer:', error);
      notify(error.response?.data?.detail || 'Error updating customer');
    }
  };

  const handleApproveBankAccount = async (accountNumber) => {
    try {
      await axios.post(`${API}/customers/${id}/approve-bank/${accountNumber}`);
      fetchCustomer();
    } catch (error) {
      console.error('Error approving bank account:', error);
      notify(error.response?.data?.detail || 'Error approving bank account');
    }
  };

  const addBankAccount = () => {
    setFormData({
      ...formData,
      bank_accounts: [...formData.bank_accounts, { bank_name: '', account_number: '', ifsc_code: '', branch: '', status: 'pending' }]
    });
  };

  if (loading) return <PageSkeleton />;
  if (!customer) return <div className="flex items-center justify-center h-full">Customer not found</div>;

  const displayData = editing ? formData : customer;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="customer-detail-page">
      <SCViewOnlyBanner record={customer} className="mb-4" />
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate(tp('/customers'))} className="w-10 h-10 flex items-center justify-center text-[#00a69c] hover:bg-[#E2F3F0] rounded-lg transition" data-testid="back-button">
          <ArrowLeft size={24} />
        </button>
        <PrevNextNav
          items={navItems}
          currentId={id}
          onNavigate={(newId) => navigate(tp(`/customers/${newId}`))}
          testIdPrefix="customer"
          disabled={editing}
        />
        <div className="flex-1">
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }} data-testid="customer-detail-title">
            {customer.organisation_name}
          </h1>
          <div className="flex items-center gap-3 mt-1">
            <span className="inline-block px-3 py-1 bg-[#E2F3F0] text-[#00a69c] text-sm font-bold rounded-md" data-testid="customer-code-badge">
              Code: {customer.customer_code}
            </span>
            {customer.industry_type && (
              <span className="text-sm text-[#6D8E87]">{customer.industry_type}</span>
            )}
          </div>
        </div>
        <ScGatedButton
          action="edit"
          record={customer}
          onClick={() => editing ? handleSave() : setEditing(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="edit-save-button"
        >
          <Pencil size={18} />
          {editing ? 'Save Changes' : 'Edit'}
        </ScGatedButton>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Organisation Info */}
        <div className="medical-card p-6" data-testid="org-info-card">
          <h3 className="text-lg font-semibold text-[#052A23] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Buildings size={20} className="text-[#00a69c]" />
            Organisation Details
          </h3>
          <div className="space-y-3">
            {[
              { label: 'Organisation Name', key: 'organisation_name' },
              { label: 'MD/CEO Name', key: 'md_ceo_name' },
              { label: 'POC Name', key: 'poc_name' },
              { label: 'POC Designation', key: 'poc_designation' },
              { label: 'Industry Type', key: 'industry_type' },
              { label: 'Website', key: 'website' },
            ].map(({ label, key }) => (
              <div key={key} className="flex justify-between items-center">
                <span className="text-sm text-[#6D8E87]">{label}:</span>
                {editing ? (
                  <input type="text" value={displayData[key] || ''}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    className="w-1/2 px-3 py-1 border border-[#D5E8E4] rounded-lg text-sm focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    data-testid={`edit-${key}`} />
                ) : (
                  <span className="text-sm font-medium text-[#052A23]">{displayData[key] || '-'}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact & Address */}
        <div className="medical-card p-6" data-testid="contact-info-card">
          <h3 className="text-lg font-semibold text-[#052A23] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <IdentificationCard size={20} className="text-[#00a69c]" />
            Contact & Address
          </h3>
          <div className="space-y-3">
            {[
              { label: 'Phone', key: 'phone' },
              { label: 'Email', key: 'email' },
              { label: 'Address', key: 'address' },
              { label: 'City', key: 'city' },
              { label: 'State', key: 'state' },
              { label: 'GST Number', key: 'gst_number' },
              { label: 'PAN Number', key: 'pan_number' },
            ].map(({ label, key }) => (
              <div key={key} className="flex justify-between items-center">
                <span className="text-sm text-[#6D8E87]">{label}:</span>
                {editing ? (
                  <input type="text" value={displayData[key] || ''}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    className="w-1/2 px-3 py-1 border border-[#D5E8E4] rounded-lg text-sm focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    data-testid={`edit-${key}`} />
                ) : (
                  <span className="text-sm font-medium text-[#052A23]">{displayData[key] || '-'}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bank Accounts - Full width */}
        <div className="medical-card p-6 lg:col-span-2" data-testid="bank-accounts-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#052A23] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Bank size={20} className="text-[#00a69c]" />
              Bank Accounts
            </h3>
            {editing && (
              <button onClick={addBankAccount} className="text-sm text-[#00a69c] hover:underline font-medium" data-testid="add-bank-btn">
                + Add Bank Account
              </button>
            )}
          </div>
          {displayData.bank_accounts?.length > 0 ? (
            <div className="space-y-3">
              {displayData.bank_accounts.map((ba, index) => (
                <div key={index} className={`p-4 rounded-lg border ${ba.status === 'pending' ? 'border-amber-300 bg-amber-50' : 'border-[#D5E8E4] bg-[#FAFFFE]'}`} data-testid={`bank-detail-${index}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-[#052A23]">{ba.bank_name || 'Bank ' + (index + 1)}</span>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ba.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {ba.status === 'approved' ? 'APPROVED' : 'PENDING APPROVAL'}
                      </span>
                      {ba.status === 'pending' && canApproveBankAccounts && !editing && (
                        <button onClick={() => handleApproveBankAccount(ba.account_number)}
                          className="px-3 py-1 bg-[#00a69c] text-white text-xs rounded-lg hover:bg-[#008f86] transition"
                          data-testid={`approve-bank-${index}`}>
                          Approve
                        </button>
                      )}
                    </div>
                  </div>
                  {editing ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                      <input type="text" placeholder="Bank Name" value={ba.bank_name}
                        onChange={(e) => { const u = [...formData.bank_accounts]; u[index].bank_name = e.target.value; setFormData({...formData, bank_accounts: u}); }}
                        className="px-3 py-1 border border-[#D5E8E4] rounded-lg text-sm" />
                      <input type="text" placeholder="Account No" value={ba.account_number}
                        onChange={(e) => { const u = [...formData.bank_accounts]; u[index].account_number = e.target.value; setFormData({...formData, bank_accounts: u}); }}
                        className="px-3 py-1 border border-[#D5E8E4] rounded-lg text-sm" />
                      <input type="text" placeholder="IFSC" value={ba.ifsc_code}
                        onChange={(e) => { const u = [...formData.bank_accounts]; u[index].ifsc_code = e.target.value; setFormData({...formData, bank_accounts: u}); }}
                        className="px-3 py-1 border border-[#D5E8E4] rounded-lg text-sm" />
                      <input type="text" placeholder="Branch" value={ba.branch}
                        onChange={(e) => { const u = [...formData.bank_accounts]; u[index].branch = e.target.value; setFormData({...formData, bank_accounts: u}); }}
                        className="px-3 py-1 border border-[#D5E8E4] rounded-lg text-sm" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm mt-1">
                      <div><span className="text-[#6D8E87]">Account:</span> <span className="text-[#052A23]">{ba.account_number}</span></div>
                      <div><span className="text-[#6D8E87]">IFSC:</span> <span className="text-[#052A23]">{ba.ifsc_code}</span></div>
                      <div><span className="text-[#6D8E87]">Branch:</span> <span className="text-[#052A23]">{ba.branch}</span></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#6D8E87]">No bank accounts added yet.</p>
          )}
        </div>

        {/* GST Compliance Card */}
        <div className="medical-card p-6 lg:col-span-2" data-testid="gst-compliance-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#052A23] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <IdentificationCard size={20} className="text-[#00a69c]" />
              GST Compliance Status
            </h3>
            {gstStatus?.valid && (
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                gstStatus.filing_health === 'good' ? 'bg-green-100 text-green-700' :
                gstStatus.filing_health === 'warning' ? 'bg-amber-100 text-amber-700' :
                gstStatus.filing_health === 'poor' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-600'
              }`} data-testid="detail-filing-health">
                {gstStatus.filing_health === 'good' ? 'COMPLIANT' :
                 gstStatus.filing_health === 'warning' ? 'PARTIALLY COMPLIANT' :
                 gstStatus.filing_health === 'poor' ? 'NON-COMPLIANT' : 'DATA UNAVAILABLE'}
              </span>
            )}
          </div>

          {gstChecking ? (
            <p className="text-sm text-[#6D8E87]">Checking GST compliance...</p>
          ) : gstStatus?.valid ? (
            <div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="p-3 bg-[#F4F9F7] rounded-lg">
                  <p className="text-xs text-[#6D8E87]">GSTIN</p>
                  <p className="text-sm font-bold text-[#052A23]">{customer.gst_number}</p>
                </div>
                <div className="p-3 bg-[#F4F9F7] rounded-lg">
                  <p className="text-xs text-[#6D8E87]">Status</p>
                  <p className={`text-sm font-bold ${gstStatus.status?.toLowerCase() === 'active' ? 'text-green-600' : 'text-[#052A23]'}`}>
                    {gstStatus.status || 'Verified'}
                  </p>
                </div>
                <div className="p-3 bg-[#F4F9F7] rounded-lg">
                  <p className="text-xs text-[#6D8E87]">Registration Date</p>
                  <p className="text-sm font-bold text-[#052A23]">{gstStatus.registration_date || 'N/A'}</p>
                </div>
                <div className="p-3 bg-[#F4F9F7] rounded-lg">
                  <p className="text-xs text-[#6D8E87]">Last Filing Date</p>
                  <p className="text-sm font-bold text-[#052A23]">{gstStatus.last_filing_date || 'N/A'}</p>
                </div>
              </div>

              {gstStatus.filing_status && gstStatus.filing_status.length > 0 ? (
                <div data-testid="detail-filing-table">
                  <h4 className="text-sm font-semibold text-[#052A23] mb-2">Recent Return Filings</h4>
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F9F7]">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs text-[#3B5A54]">Return Type</th>
                        <th className="text-left px-3 py-2 text-xs text-[#3B5A54]">Period</th>
                        <th className="text-left px-3 py-2 text-xs text-[#3B5A54]">Status</th>
                        <th className="text-left px-3 py-2 text-xs text-[#3B5A54]">Date of Filing</th>
                        <th className="text-left px-3 py-2 text-xs text-[#3B5A54]">Mode</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gstStatus.filing_status.map((f, i) => (
                        <tr key={i} className="border-t border-[#D5E8E4]">
                          <td className="px-3 py-2 font-medium">{f.return_type}</td>
                          <td className="px-3 py-2 text-[#6D8E87]">{f.period}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              f.status?.toLowerCase() === 'filed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                            }`}>{f.status || 'N/A'}</span>
                          </td>
                          <td className="px-3 py-2 text-[#6D8E87]">{f.date_of_filing || '-'}</td>
                          <td className="px-3 py-2 text-[#6D8E87]">{f.mode || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-[#6D8E87]">Detailed filing data not available from the free tier API. GSTIN format and state verified locally.</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-[#6D8E87]">{customer.gst_number ? 'Unable to fetch GST compliance data.' : 'No GST number on file.'}</p>
          )}
        </div>

        {/* Notes */}
        <div className="medical-card p-6 lg:col-span-2" data-testid="notes-card">
          <h3 className="text-lg font-semibold text-[#052A23] mb-3" style={{ fontFamily: 'Outfit' }}>Notes</h3>
          {editing ? (
            <textarea value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none" rows={3} />
          ) : (
            <p className="text-sm text-[#3B5A54]">{customer.notes || 'No notes added.'}</p>
          )}
        </div>

        {/* iter132.3 — Saved Payment Terms templates for this customer */}
        <div className="medical-card p-6 lg:col-span-2" data-testid="saved-payment-terms-card">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <h3 className="text-lg font-semibold text-[#052A23] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <CheckCircle size={20} /> Saved Payment Terms
              </h3>
              <p className="text-xs text-[#6D8E87] mt-1">
                VP-approved combinations for this customer. Any future Won that matches one of these skips the approval queue.
              </p>
            </div>
            {!canRevokeTermsTemplate && termsTemplates.length > 0 && (
              <span className="text-[11px] text-[#6D8E87] italic whitespace-nowrap">VP-only revoke</span>
            )}
          </div>

          {termsTemplates.length === 0 ? (
            <p className="text-sm text-[#6D8E87]" data-testid="saved-payment-terms-empty">
              No saved payment terms templates for this customer yet. Anything the VP approves for this customer will show up here.
            </p>
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-[#6D8E87]">
                  <tr>
                    <th className="text-left px-2 py-2">Combination</th>
                    <th className="text-left px-2 py-2">Approved By</th>
                    <th className="text-right px-2 py-2">Reuse</th>
                    <th className="text-right px-2 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {termsTemplates.map((tpl) => {
                    const legs = tpl.payment_terms || {};
                    const chips = [];
                    if (legs.advance_with_po?.enabled) chips.push({ k: 'advance_with_po', label: `Adv ${legs.advance_with_po.percent}%`, tone: 'emerald' });
                    if (legs.before_dispatch?.enabled) chips.push({ k: 'before_dispatch', label: `Dispatch ${legs.before_dispatch.percent}%`, tone: 'emerald' });
                    if (legs.on_installation?.enabled) chips.push({ k: 'on_installation', label: `Install ${legs.on_installation.percent}%`, tone: 'amber' });
                    if (legs.credit?.enabled) chips.push({ k: 'credit', label: `Credit ${legs.credit.percent}%/${legs.credit.days || 0}d`, tone: 'rose' });
                    const approvedAt = tpl.last_approved_at || tpl.created_at;
                    return (
                      <tr key={tpl.id} className="border-t border-[#EAF3F0] hover:bg-[#FAFDFC]" data-testid={`saved-payment-terms-row-${tpl.id}`}>
                        <td className="px-2 py-3">
                          <div className="flex flex-wrap gap-1.5 mb-1">
                            {chips.map((c) => (
                              <span
                                key={c.k}
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                  c.tone === 'emerald' ? 'bg-emerald-100 text-emerald-800' :
                                  c.tone === 'amber' ? 'bg-amber-100 text-amber-800' :
                                  'bg-rose-100 text-rose-800'
                                }`}
                              >
                                {c.label}
                              </span>
                            ))}
                          </div>
                          <div className="text-[11px] text-[#6D8E87]">{tpl.payment_terms_summary}</div>
                        </td>
                        <td className="px-2 py-3 text-[#3B5A54]">
                          <div className="text-sm">{tpl.approved_by_name || tpl.last_approved_by_name || '—'}</div>
                          <div className="text-[11px] text-[#6D8E87]">
                            {approvedAt ? new Date(approvedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                          </div>
                        </td>
                        <td className="px-2 py-3 text-right tabular-nums text-[#052A23] font-semibold">
                          {tpl.reuse_count || 0}
                          {tpl.last_reused_at && (
                            <div className="text-[10px] font-normal text-[#6D8E87]">
                              last {new Date(tpl.last_reused_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-3 text-right">
                          {canRevokeTermsTemplate ? (
                            <button
                              type="button"
                              disabled={termsBusyId === tpl.id}
                              onClick={() => handleRevokeTemplate(tpl)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                              data-testid={`saved-payment-terms-revoke-${tpl.id}`}
                              title="Revoke this template — future Wons on this combo will need VP approval again"
                            >
                              <Trash size={12} weight="bold" /> Revoke
                            </button>
                          ) : (
                            <span className="text-[11px] text-[#6D8E87] italic">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {termsRevoked.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[#EAF3F0]" data-testid="revoked-payment-terms-section">
              <h4 className="text-sm font-semibold text-[#052A23] flex items-center gap-2 mb-2">
                <Clock size={16} /> Recently Revoked <span className="text-[11px] text-[#6D8E87] font-normal">(28-day undo window)</span>
              </h4>
              <ul className="space-y-2">
                {termsRevoked.map((tpl) => {
                  const exp = tpl.revoke_expires_at ? new Date(tpl.revoke_expires_at) : null;
                  const daysLeft = exp ? Math.max(0, Math.ceil((exp.getTime() - Date.now()) / 86400000)) : 0;
                  return (
                    <li
                      key={tpl.id}
                      className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-rose-50/40 border border-rose-100"
                      data-testid={`revoked-payment-terms-row-${tpl.id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-[#052A23] truncate line-through">
                          {tpl.payment_terms_summary}
                        </div>
                        <div className="text-[11px] text-[#6D8E87]">
                          Revoked by {tpl.revoked_by_name || '—'} · restore within{' '}
                          <span className={daysLeft <= 3 ? 'text-rose-700 font-semibold' : ''}>{daysLeft}d</span>
                        </div>
                      </div>
                      {canRevokeTermsTemplate ? (
                        <button
                          type="button"
                          disabled={termsBusyId === tpl.id || daysLeft === 0}
                          onClick={() => handleRestoreTemplate(tpl)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded border border-emerald-300 text-emerald-800 hover:bg-emerald-50 disabled:opacity-60"
                          data-testid={`revoked-payment-terms-restore-${tpl.id}`}
                          title="Restore this template within the undo window"
                        >
                          <ArrowCounterClockwise size={12} weight="bold" /> Restore
                        </button>
                      ) : (
                        <span className="text-[11px] text-[#6D8E87] italic whitespace-nowrap">VP-only</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;
